import { ApiResponse, PaymentMethod } from 'vopay';
import { useApi } from '#shared/providers/Api';
import { useAccountingSoftware } from './AccountingSoftwareContext';

type AddInteracParams = {
  accountID: string;
  recipientName: string;
  email: string;
  phone: string;
  question: string;
  answer: string;
};

// The slice of the portal's usePaymentMethods the asp save-flow needs:
// set-default, delete and add-Interac for the method types the Banking Update
// dialogs persist (bank, Interac, cards). Same endpoints and payloads as the
// portal hook so both surfaces keep identical behaviour.
export default function usePaymentMethodOps() {
  const api = useApi();
  const { account } = useAccountingSoftware();
  const loginUserName = account.LoginUserName || '';
  const clientAccountsEnabled = Boolean(account.ClientAccountsEnabled);

  function setDefaultMethod(
    method: PaymentMethod['PaymentMethod'],
    accountToken: string,
    accountID: string,
  ): Promise<ApiResponse> {
    if (method === 'BankAccount') {
      return api.postSetMyClientBankAccount({
        ClientAccountID: accountID,
        SetAsDefault: true,
        Token: accountToken,
        PortalUser: loginUserName,
      });
    }

    if (method === 'CreditCard') {
      return api.postSetClientMyCreditCard({
        CreditCardToken: accountToken,
        SetAsDefault: true,
        ClientAccountID: accountID,
      });
    }

    if (method === 'DebitCard') {
      return api.postSetClientMyDebitCard({
        CardToken: accountToken,
        SetAsDefault: true,
        ClientAccountID: accountID,
      });
    }

    if (method === 'Email' || method === 'PhoneNumber') {
      return api.postSetClientEmail({
        Token: accountToken,
        ClientAccountID: accountID,
        SetAsDefault: true,
      });
    }

    return Promise.resolve({ Success: false, ErrorMessage: 'Invalid payment method' });
  }

  function deleteMethod(method: Pick<PaymentMethod, 'PaymentMethod' | 'Token'>, accountID: string) {
    if (method.PaymentMethod === 'BankAccount') {
      return api.postDeleteClientBankAccount({
        PortalUser: loginUserName,
        Token: method.Token,
      });
    }

    if (method.PaymentMethod === 'Email' || method.PaymentMethod === 'PhoneNumber') {
      return api.postDeleteInteracEmail({ Token: method.Token, ClientAccountID: accountID });
    }

    return Promise.resolve({ Success: false, ErrorMessage: 'Invalid payment method' });
  }

  function addInterac({ accountID, recipientName, email, phone, question, answer }: AddInteracParams) {
    return api.postAddInteracEmail({
      PortalUser: loginUserName,
      [clientAccountsEnabled ? 'ClientAccountID' : 'ContactID']: accountID,
      RecipientName: recipientName,
      RecipientEmailAddress: email,
      RecipientPhoneNumber: phone,
      Question: question,
      Answer: answer,
      ClientControlled: clientAccountsEnabled,
    });
  }

  return { setDefaultMethod, deleteMethod, addInterac };
}
